import type { Middleware } from 'redux';

import {
  identifyPostHogUser,
  resetPostHogIdentity,
} from '@lib/analytics/posthogIdentity';
import { isPostHogLoaded, posthog } from '@lib/posthog.lib';
import { EverysetUser } from '@lib/types/user.types';

import {
  resetStore,
  setImpersonatedUserId,
  setUserProfile,
} from './slices/appSlice';

type AnalyticsAction = {
  type: string;
  payload?: EverysetUser | number | null;
};

type AnalyticsStoreState = {
  app: {
    user: {
      impersonatedUserId?: number | null;
    };
  };
};

/**
 * Redux middleware that maps Jobs App auth-related actions to PostHog
 * identify / reset / impersonation events.
 *
 * During impersonation the admin remains the PostHog identity; only an
 * event is captured for the impersonated target.
 */
export const analyticsMiddleware: Middleware =
  (store) => (next) => (action: AnalyticsAction) => {
    const previousState = store.getState() as AnalyticsStoreState;
    const result = next(action);

    switch (action?.type) {
      case setUserProfile.type: {
        const state = store.getState() as AnalyticsStoreState;
        const isImpersonating = Boolean(state.app.user.impersonatedUserId);

        if (!isImpersonating) {
          const profile = action.payload as EverysetUser | undefined;

          identifyPostHogUser(profile?.id, {
            email: profile?.email ?? null,
          });
        }

        break;
      }

      case setImpersonatedUserId.type: {
        const impersonatedTargetId = action.payload;
        const previousImpersonatedUserId =
          previousState.app.user.impersonatedUserId;

        if (
          isPostHogLoaded() &&
          typeof impersonatedTargetId === 'number' &&
          Number.isFinite(impersonatedTargetId) &&
          impersonatedTargetId > 0 &&
          impersonatedTargetId !== previousImpersonatedUserId
        ) {
          posthog.capture('Impersonation Started', {
            impersonated_target_id: String(impersonatedTargetId),
          });
        }

        break;
      }

      case resetStore.type:
        resetPostHogIdentity();
        break;

      default:
        break;
    }

    return result;
  };
